// Status grouping constants for Tracker - mirrors pipeline stages
// This allows us to group affaires by their status and apply group-specific rules

export interface StatusGroup {
  id: number;
  label: string;
  description: string;
  color: string;
  icon: string;
}

// Pipeline stages used for status grouping
export const STATUS_GROUPS: StatusGroup[] = [
  { id: 10, label: 'Bureau à encoder', description: 'Dossier tout juste créé', color: '#ff6b6b', icon: '📋' },
  { id: 20, label: 'Bureau à planifier', description: 'Préparation / prise de rendez-vous', color: '#ff8e53', icon: '🔍' },
  { id: 30, label: 'RV en attente', description: 'RV en attente de planification', color: '#f39c12', icon: '📊' },
  { id: 40, label: 'Expert à visiter', description: 'Visite terrain à organiser', color: '#e67e22', icon: '✅' },
  { id: 50, label: 'Expert à achever', description: 'Rapport d\'expertise à boucler', color: '#f1c40f', icon: '📝' },
  { id: 60, label: 'Bureau à valoriser', description: 'Rapport d\'expertise à valoriser', color: '#52c41a', icon: '🏁' },
  { id: 70, label: 'Bureau à achever', description: 'Rapport d\'expertise à achever', color: '#1890ff', icon: '💼' },
  { id: 80, label: 'Attente doc/infos', description: 'Attentes administratives', color: '#722ed1', icon: '⚡' },
  { id: 90, label: 'Attente paiement', description: 'Attentes paiements, relances', color: '#eb2f96', icon: '📧' },
  { id: 100, label: 'Att. confirmation', description: 'Validations externes', color: '#13c2c2', icon: '✔️' },
  { id: 110, label: 'À transmettre', description: 'Prêt à être envoyé', color: '#52c41a', icon: '📤' },
  { id: 999, label: 'Autres', description: 'Archives, terminés et autres statuts', color: '#94a3b8', icon: '📦' }
];

// Status string to group ID mapping (from pipeline)
export const STATUS_TO_GROUP_MAPPING: Record<string, number> = {
  // Stage 10 - Dossier tout juste créé
  'bureau à encoder': 10,
  'bureau a encoder': 10,

  // Stage 20 - Préparation / prise de rendez-vous
  'bureau à planifier': 20,
  'bureau a planifier': 20,

  // Stage 30 - RV en attente de planification
  'rv en attente*': 30,

  // Stage 40 - Visite terrain à organiser
  'expert à visiter': 40,
  'expert a visiter': 40,

  // Stage 50 - Rapport d'expertise à boucler
  'expert à achever': 50,
  'expert a achever': 50,

  // Stage 60 - Rapport d'expertise à valoriser
  'bureau à valoriser': 60,
  'bureau a valoriser': 60,

  // Stage 70 - Rapport d'expertise à achever
  'bureau à achever': 70,
  'bureau a achever': 70,
  '*à achever': 70,
  '*a achever': 70,

  // Stage 80 - Attentes administratives: docs
  'attente doc/infos': 80,
  'attente documents/infos': 80,

  // Stage 90 - Attentes paiements
  'attente paiement*': 90,
  'attente de paiement + conf': 90,
  'demande annulee + att paiement client': 90,
  'attente de facturation': 90,
  '*rappel paiement client': 90,
  '1er rappel paiement courtier': 90,
  '2eme rappel paiement courtier': 90,
  '3eme rappel paiement courtier sms': 90,
  '3eme rappel tf + mail (menace solvency)': 90,
  '*rappel paiement courtier*': 90,
  'attente de facturation mensuelle': 90,
  'solvency': 90,

  // Stage 100 - Validations externes
  'attente confirmation*': 100,
  'facture en attente confirmation': 100,
  'mandat de vente en cours': 100,

  // Stage 110 - Prêt à être envoyé
  'à transmettre': 110,
  'a transmettre': 110
};

/**
 * Maps a status string to its group ID
 * Returns 999 (Autres) for unmapped statuses, undefined only if status is null/undefined
 */
export function getStatusGroupId(status: string | null | undefined): number | undefined {
  if (!status) return undefined;

  const normalized = status.toLowerCase().trim();

  // Direct match
  if (STATUS_TO_GROUP_MAPPING[normalized] !== undefined) {
    return STATUS_TO_GROUP_MAPPING[normalized];
  }

  // Wildcard matching (e.g., "rv en attente*" matches "rv en attente quelque chose")
  for (const [pattern, groupId] of Object.entries(STATUS_TO_GROUP_MAPPING)) {
    if (pattern.includes('*')) {
      const regex = new RegExp('^' + pattern.replace(/\*/g, '.*') + '$', 'i');
      if (regex.test(normalized)) {
        return groupId;
      }
    }
  }

  // If no match found, assign to "Autres" group (999)
  return 999;
}

/**
 * Gets the status group object for a given status string
 */
export function getStatusGroup(status: string | null | undefined): StatusGroup | undefined {
  const groupId = getStatusGroupId(status);
  if (groupId === undefined) return undefined;

  return STATUS_GROUPS.find(g => g.id === groupId);
}

/**
 * Gets all status groups, optionally filtered by IDs
 */
export function getStatusGroups(groupIds?: number[]): StatusGroup[] {
  if (!groupIds || groupIds.length === 0) {
    return STATUS_GROUPS;
  }
  return STATUS_GROUPS.filter(g => groupIds.includes(g.id));
}
